import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { FC, useMemo } from 'react'
import useSWR from 'swr'

import { useChinaDetector } from '../../common/region-locale'
import {
  buildSWRKey,
  CommonData,
  getPageCommonData,
  getNavigationThreeJump,
  NewThreeJumpItem,
} from '../../service/common.service'
import PdImage from '../pd-image/pd-image'
import Popover from '../pd-popover/pd-popover'
import Subscribe from '../pd-subscribe/pd-subscribe'
import { Toaster } from 'react-hot-toast'
import { EventReportManager } from '../../event-report/event-report-manager'
import type { FooterPopoverVisibleMask } from './popover-mask'
import { hideFooterPopover, isFooterPopoverVisible, showFooterPopover } from './popover-mask'
import { isExternalHttpHref } from '@/common/link-rel-helper'
import { resolveMediaAlt } from '@/common/media-alt'

interface Menu {
  title: string
  link: string
  links: Array<{ name: string; link: string }>
}

/**
 * 判断底部栏子项链接是否为站外链接：仅第三方 http(s) 链接视为外链。
 */
function isExternalHref(href: string): boolean {
  return isExternalHttpHref(href)
}

const PdFooter: FC<{ hideChangeLang?: boolean }> = ({ hideChangeLang }) => {
  const { locale } = useRouter()
  const t = useTranslations()
  const inChina = useChinaDetector()
  const { data, error } = useSWR<CommonData>(buildSWRKey(locale as string), () => getPageCommonData(locale as string))
  const { data: threeJumpData } = useSWR<NewThreeJumpItem[]>(getNavigationThreeJump.swrkey(locale as string), () =>
    getNavigationThreeJump(locale as string)
  )

  const [expandMenu, setExpandMenu] = useState<[index: number, expand: boolean]>([-1, false])
  const [mobilePopupIndex, setMobilePopupIndex] = useState<number>(-1)

  const privacyUrl = useMemo(() => {
    if (inChina) {
      return 'https://www.pudutech.com/privacy'
    } else {
      return 'https://www.pudurobotics.com/' + locale + '/privacy'
    }
  }, [inChina, locale])

  const MenuList: Array<Menu> = useMemo(() => {
    if (!data) {
      return []
    }

    return data.bottomNav?.map((nav) => {
      return {
        title: nav.title,
        link: nav.jumpUrl,
        links: nav.item
          .map((item) => ({ name: item.title, link: item.jumpUrl, type: item.type }))
          .filter((item) => item.type !== 'pudubot_vslam'),
      }
    })
  }, [data])

  const SocialIcons: Array<{
    title: string
    icon: string
    iconAlt?: string
    activeIcon: string
    activeIconAlt?: string
    hoverIcon: string
    hoverIconAlt?: string
    href: string
    isJump: boolean
  }> = useMemo(() => {
    if (!threeJumpData) {
      return []
    }

    return threeJumpData.map((tj) => ({
      icon: tj.mediaUrl?.url ?? '',
      iconAlt: resolveMediaAlt(tj.mediaUrl?.alts, locale),
      activeIcon: tj.activeMediaUrl?.url ?? '',
      activeIconAlt: resolveMediaAlt(tj.activeMediaUrl?.alts, locale),
      hoverIcon: tj.hoverMediaUrl?.url ?? '',
      hoverIconAlt: resolveMediaAlt(tj.hoverMediaUrl?.alts, locale),
      href: tj.jumpUrl,
      isJump: tj.isJump,
      title: tj.title,
    }))
  }, [locale, threeJumpData])

  // 点击了社交媒体按钮
  function handleIconClick(platform: string) {
    const pageUrl = window.location.href
    const buttonPosition = 'footer'
    EventReportManager.getInstance().fireEventImmediately(
      { data: { event: 'social_click', platform, page_url: pageUrl, button_position: buttonPosition } },
      true
    )
  }

  /* 微信 */
  /** 桌面端非跳转社媒二维码弹窗可见状态，使用位掩码隔离不同图标的 hover 事件。 */
  const [popoverVisibleMask, setPopoverVisibleMask] = useState<FooterPopoverVisibleMask>(0)

  /**
   * 打开指定社媒二维码弹窗并上报社媒点击事件。
   *
   * @param index - 当前悬停的社媒图标下标。
   * @returns 无返回值。
   * @throws 当下标不是合法图标下标时，由弹窗状态工具抛出异常。
   */
  function openPopover(index: number) {
    setPopoverVisibleMask((currentMask) => showFooterPopover(currentMask, index))
    const social = SocialIcons[index]

    if (social) {
      const platform = social.title
      const pageUrl = window.location.href
      const buttonPosition = 'footer'
      EventReportManager.getInstance().fireEventImmediately(
        { data: { event: 'social_click', platform, page_url: pageUrl, button_position: buttonPosition } },
        true
      )
    }
  }

  /**
   * 关闭指定社媒二维码弹窗。
   *
   * @param index - 当前移出的社媒图标下标。
   * @returns 无返回值。
   * @throws 当下标不是合法图标下标时，由弹窗状态工具抛出异常。
   */
  function closePopover(index: number) {
    setPopoverVisibleMask((currentMask) => hideFooterPopover(currentMask, index))
  }

  /**
   * 判断指定社媒二维码弹窗是否展示。
   *
   * @param index - 需要判断的社媒图标下标。
   * @returns 指定社媒二维码弹窗是否展示。
   * @throws 当下标不是合法图标下标时，由弹窗状态工具抛出异常。
   */
  function testPopover(index: number): boolean {
    return isFooterPopoverVisible(popoverVisibleMask, index)
  }

  return (
    <>
      <Toaster />
      <div
        className={[
          'pd-footer relative z-20',
          'hidden',
          'laptop:flex',
          'flex-col items-center w-full pt-24 pb-16 bg-[#e6e9ed]',
        ].join(' ')}
      >
        <div className="flex justify-start w-full max-w-300 mx-auto">
          <div className="w-76 mr-4">
            <div className="w-78px h-6">
              <PdImage src="/logo.svg" width="100%" height="100%" alt="logo" object="contain" enableMask={false} />
            </div>
            <div className="mt-24px">
              <Subscribe />
            </div>
          </div>

          <div className={['flex-1 flex flex-wrap justify-between text-xs', inChina ? 'laptop:ml-67' : ''].join(' ')}>
            {MenuList?.map((menu, index) => (
              <div key={menu.title + index}>
                <Link href={menu.link}>
                  <a className="font-semibold pb-6 inline-block">{menu.title}</a>
                </Link>
                <ul className="flex flex-col gap-2 text-pd-gray">
                  {menu.links.map((link, index) => (
                    <li key={link.name + index} className="hover:underline">
                      {/* 严格按优先级渲染：/contact-us → http(s) 外链 → 站内 next/link */}
                      {link.link === '/contact-us' && (
                        <Link href={link.link}>
                          <a className="event-trigger" data-event-key="event" data-event-value="initiate_lead">
                            {link.name}
                          </a>
                        </Link>
                      )}
                      {link.link !== '/contact-us' && isExternalHref(link.link) && (
                        <a href={link.link} target="_blank" rel="noopener noreferrer nofollow">
                          {link.name}
                        </a>
                      )}
                      {link.link !== '/contact-us' && !isExternalHref(link.link) && (
                        <Link href={link.link}>
                          <a>{link.name}</a>
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full max-w-300 flex justify-end gap-2 pt-16 pb-4 border-b border-[#d4d8e0]">
          {SocialIcons?.map((item, index) => {
            if (item.isJump) {
              return (
                <a
                  key={item.title + index}
                  className="group relative w-8 h-8 inline-block transition-transform duration-150 active:scale-[0.6]"
                  href={item.href}
                  target="_blank"
                  rel="noreferrer nofollow noopener"
                  title={item.title}
                  onClick={() => handleIconClick(item.title)}
                >
                  <div className="absolute inset-0 transition-opacity duration-200 group-hover:opacity-0">
                    <PdImage
                      src={item.icon}
                      alt={item.iconAlt ?? item.title}
                      enableMask={false}
                      width="100%"
                      height="100%"
                      object="contain"
                    />
                  </div>
                  <div className="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                    <PdImage
                      src={item.activeIcon}
                      alt={item.activeIconAlt ?? item.title}
                      enableMask={false}
                      width="100%"
                      height="100%"
                      object="contain"
                    />
                  </div>
                </a>
              )
            }

            return (
              <div
                key={item.title + index}
                className="group relative w-8 h-8 cursor-pointer transition-transform duration-150 active:scale-[0.6]"
                title={item.title}
                onMouseEnter={() => openPopover(index)}
                onMouseLeave={() => closePopover(index)}
              >
                <Popover
                  show={testPopover(index)}
                  content={
                    <div className="w-40 h-40 relative">
                      <PdImage src={item.hoverIcon} alt={item.hoverIconAlt} enableMask={false} width={'100%'} />
                    </div>
                  }
                >
                  <div className="relative w-full h-full">
                    <div className="absolute inset-0 transition-opacity duration-200 group-hover:opacity-0">
                      <PdImage
                        src={item.icon}
                        alt={item.iconAlt ?? item.title}
                        enableMask={false}
                        width="100%"
                        height="100%"
                        object="contain"
                      />
                    </div>
                    <div className="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      <PdImage
                        src={item.activeIcon}
                        alt={item.activeIconAlt ?? item.title}
                        enableMask={false}
                        width="100%"
                        height="100%"
                        object="contain"
                      />
                    </div>
                  </div>
                </Popover>
              </div>
            )
          })}
        </div>

        <div className="w-full max-w-300 flex justify-between pt-4 text-xs text-pd-gray">
          <p className="flex items-center justify-start">
            <span>
              {inChina
                ? 'Copyright © 2026 Pudu Robotics 普渡机器人 版权所有'
                : 'Copyright © 2026 Pudu Robotics All Rights Reserved.'}
            </span>
            {inChina && (
              <>
                <a
                  href={'https://beian.miit.gov.cn/#/Integrated/index'}
                  target="_blank"
                  rel="noreferrer nofollow noopener"
                >
                  <span className="mx-4 cursor-pointer">粤ICP备16033559号</span>
                </a>
                <a
                  href="https://beian.mps.gov.cn/#/query/webSearch?code=44030002007879"
                  rel="noreferrer nofollow noopener"
                  target="_blank"
                >
                  <span className="mx-4 cursor-pointer">粤公网安备44030002007879号</span>
                </a>
              </>
            )}
            <Link href={'/privacy'}>
              <a
                className="cursor-pointer laptop:ml-8"
                // href={privacyUrl}
                href="/privacy"
                target="_blank"
                rel="noreferrer"
              >
                {t('Privacy')}
              </a>
            </Link>
            <Link href={'/sitemap'}>
              <a className="cursor-pointer laptop:ml-8">{t('Sitemap')}</a>
            </Link>
          </p>
        </div>

        {locale === 'zh-CN' && (
          <div className={['w-full max-w-300 flex justify-between pt-2 text-xs text-pd-gray'].join(' ')}>
            {'深圳市南山区西丽街道西丽社区打石一路深圳国际创新谷三栋1101'}
          </div>
        )}
      </div>

      {/* for mobile device */}
      <div className="pd-footer relative z-20 laptop:hidden w-full px-6 pt-39px pb-20 flex flex-col items-start bg-[#e6e9ed]">
        <div className="w-78px h-6  laptop:w-94px laptop:h-30px">
          <PdImage src={'/logo.svg'} enableMask={false} width="100%" height="100%" object="contain" alt="logo" />
          <div className="mt-12px">
            <Subscribe />
          </div>
        </div>

        {/* menu groups */}
        <ul className="mt-24 w-full text-black/90 text-sm">
          {MenuList?.map((menu, index) => (
            <li
              key={menu.title + index}
              className={[
                'w-full border-t border-[#576c8a] transition-all ease-in-out duration-500 h-72px overflow-hidden',
                // (expandMenu[0] === index && expandMenu[1]) ? '!h-fit' : '',
                index === MenuList.length - 1 ? 'border-b' : '',
              ].join(' ')}
              style={{ height: expandMenu[0] === index && expandMenu[1] ? menu.links.length * 2.25 + 4.5 + 'rem' : '' }}
            >
              {menu.links && menu.links.length > 0 && (
                <>
                  <h5
                    className="font-semibold h-72px leading-72px flex justify-between items-center"
                    onClick={() => setExpandMenu([index, index === expandMenu[0] ? !expandMenu[1] : true])}
                  >
                    <span>{menu.title}</span>
                    <i
                      className={[
                        'w-3 h-3',
                        expandMenu[0] === index && expandMenu[1] ? 'rotate-45' : '',
                        'inline-block bg-no-repeat bg-[url("/icons/icon_add.svg")] bg-contain transition-all ease-in-out duration-500',
                      ].join(' ')}
                    ></i>
                  </h5>

                  <ul className="w-full pl-4 flex flex-col gap-4 text-pd-gray">
                    {menu.links.map((link, index) => (
                      <li
                        key={link.name + index}
                        className={['hover:underline', index === menu.links.length - 1 ? 'pb-4' : ''].join(' ')}
                      >
                        {/* H5 端不区分 /contact-us（独立按钮在菜单外），仅按外链/站内区分 */}
                        {isExternalHref(link.link) ? (
                          <a href={link.link} target="_blank" rel="noopener noreferrer nofollow">
                            {link.name}
                          </a>
                        ) : (
                          <Link href={link.link}>
                            <a>{link.name}</a>
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {(!menu.links || menu.links.length === 0) && (
                <Link href={menu.link}>
                  <h5 className="font-semibold h-72px leading-72px">{menu.title}</h5>
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* contact-us button */}
        <Link href={'/contact-us'}>
          <a
            className="event-trigger mt-8 w-full py-13px text-center bg-pdblue hover:bg-blue-800 hover:text-white !text-white text-base"
            data-event-key="event"
            data-event-value="initiate_lead"
          >
            {t('ContactUs')}
          </a>
        </Link>

        {/* social icons */}
        <ul className="flex flex-wrap gap-6 w-full pt-8 pb-4 border-b border-[#576c8a]">
          {SocialIcons?.map((item, index) => (
            <li key={item.title + index} className="w-8 h-8">
              {item.isJump ? (
                <a
                  href={item.href}
                  title={item.title}
                  target="_blank"
                  rel="noreferrer nofollow noopener"
                  onClick={() => handleIconClick(item.title)}
                >
                  <PdImage
                    src={item.icon}
                    alt={item.iconAlt}
                    enableMask={false}
                    width="100%"
                    height="100%"
                    object="contain"
                  />
                </a>
              ) : (
                <button className="w-full h-full" onClick={() => setMobilePopupIndex(index)}>
                  <PdImage
                    src={item.icon}
                    alt={item.iconAlt}
                    enableMask={false}
                    width="100%"
                    height="100%"
                    object="contain"
                  />
                </button>
              )}
            </li>
          ))}
        </ul>

        {/* 移动端非跳转社媒二维码弹窗 */}
        {mobilePopupIndex >= 0 && SocialIcons[mobilePopupIndex] && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setMobilePopupIndex(-1)}
          >
            <div
              className="relative bg-white rounded-xl p-6 flex flex-col items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-black/30 text-base leading-none"
                onClick={() => setMobilePopupIndex(-1)}
              >
                ✕
              </button>
              <div className="w-48 h-48 relative">
                <PdImage
                  src={SocialIcons[mobilePopupIndex].hoverIcon}
                  alt={SocialIcons[mobilePopupIndex].hoverIconAlt}
                  enableMask={false}
                  width="100%"
                  height="100%"
                  object="contain"
                />
              </div>
              <p className="text-sm text-black/60">{SocialIcons[mobilePopupIndex].title}</p>
            </div>
          </div>
        )}

        {/* copyright */}
        <article className="flex flex-col mt-3 text-black/60 text-xs">
          <span>
            {inChina
              ? 'Copyright © 2026 Pudu Robotics 普渡机器人 版权所有'
              : 'Copyright © 2026 Pudu Robotics All Rights Reserved.'}
          </span>
          <div className="flex flex-wrap ">
            {inChina && (
              <>
                <a
                  href={'https://beian.miit.gov.cn/#/Integrated/index'}
                  target="_blank"
                  rel="noreferrer nofollow noopener"
                >
                  粤ICP备16033559号
                </a>
                <a
                  href="https://beian.mps.gov.cn/#/query/webSearch?code=44030002007879"
                  rel="noreferrer nofollow noopener"
                  target="_blank"
                >
                  <span className="mx-4 cursor-pointer">粤公网安备44030002007879号</span>
                </a>
              </>
            )}

            <Link href={'/privacy'}>
              <span className="laptop:ml-8">{t('Privacy')}</span>
            </Link>
            <Link href={'/sitemap'}>
              <span className="laptop:ml-8">{t('Sitemap')}</span>
            </Link>
          </div>
        </article>
      </div>
    </>
  )
}

export default PdFooter
